import React from 'react';
import PropTypes from 'prop-types';

export const SectionHeading = ({eyebrow, number, title, titleAccent}) => (
  <header className="replayer-club-page__journey-heading">
    <span className="replayer-club-page__journey-number" aria-hidden="true">{number}</span>
    <div>
      <p>{eyebrow}</p>
      <h2>
        <span className="replayer-club-page__journey-title-primary">{title}</span>
        {titleAccent && <span className="replayer-club-page__journey-title-accent">{titleAccent}</span>}
      </h2>
    </div>
  </header>
);

SectionHeading.propTypes = {
  eyebrow: PropTypes.string.isRequired,
  number: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  titleAccent: PropTypes.string
};

SectionHeading.defaultProps = {titleAccent: null};

export const PublicPartnerSkyworkPlanner = ({children, labels, onToggle, open}) => (
  <section className="replayer-club-page__journey replayer-club-page__planner" aria-label={labels.title}>
    <SectionHeading eyebrow={labels.eyebrow} number={labels.number} title={labels.title} titleAccent={labels.titleAccent}/>
    <div className="replayer-club-page__journey-intro">
      <p>{labels.intro}</p>
      <button type="button" className="replayer-club-page__journey-primary" onClick={onToggle} aria-expanded={open}>
        {open ? labels.hide : labels.open}
      </button>
    </div>
    {open && <div className="replayer-club-page__journey-content">{children}</div>}
  </section>
);

PublicPartnerSkyworkPlanner.propTypes = {
  children: PropTypes.node,
  labels: PropTypes.object.isRequired,
  onToggle: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired
};

PublicPartnerSkyworkPlanner.defaultProps = {children: null};

export const PublicPartnerSkyworkEvents = ({counts, items, labels, onSelect}) => {
  if (items.length > 0) {
    return (
      <section id="club-special-events" className="replayer-club-page__journey replayer-club-page__events" aria-label={labels.title}>
        <SectionHeading eyebrow={labels.eyebrow} number={labels.number} title={labels.title} titleAccent={labels.titleAccent}/>
        <p className="replayer-club-page__journey-description">{labels.intro}</p>
        <div className="replayer-club-page__special-event-grid">
          {items.map(item => (
            <article className="replayer-club-page__special-event-card" key={item.id}>
              <div
                className="replayer-club-page__special-event-image"
                style={item.imageUrl ? {backgroundImage: `url(${item.imageUrl})`} : null}
                aria-hidden="true"
              >
                <span>{labels.badge}</span>
              </div>
              <div className="replayer-club-page__special-event-body">
                <div>
                  <h3>{item.title}</h3>
                  {item.description ? <p>{item.description}</p> : null}
                </div>
                <dl>
                  <div><dt>{labels.access}</dt><dd>{item.accessRange}</dd></div>
                  <div><dt>{labels.content}</dt><dd>{item.contentRange}</dd></div>
                </dl>
                <strong className="replayer-club-page__special-event-price">{item.priceLabel}</strong>
                <button type="button" onClick={() => onSelect(item.id)}>{item.actionLabel}</button>
              </div>
            </article>
          ))}
        </div>
      </section>
    );
  }

  const events = [
    {id: 'live', count: counts.live, label: labels.live},
    {id: 'replay', count: counts.replay, label: labels.available},
    {id: 'upcoming', count: counts.upcoming, label: labels.upcoming}
  ];

  return (
    <section className="replayer-club-page__journey replayer-club-page__events" aria-label={labels.title}>
      <SectionHeading eyebrow={labels.eyebrow} number={labels.number} title={labels.title} titleAccent={labels.titleAccent}/>
      <p className="replayer-club-page__journey-description">{labels.intro}</p>
      <div className="replayer-club-page__event-grid">
        {events.map(event => (
          <button type="button" key={event.id} onClick={() => onSelect(event.id)} disabled={event.count === 0}>
            <span>{String(event.count).padStart(2, '0')}</span>
            <strong>{event.label}</strong>
            <small>{event.count === 0 ? labels.empty : labels.view}</small>
          </button>
        ))}
      </div>
    </section>
  );
};

PublicPartnerSkyworkEvents.propTypes = {
  counts: PropTypes.shape({live: PropTypes.number, replay: PropTypes.number, upcoming: PropTypes.number}).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    accessRange: PropTypes.string.isRequired,
    actionLabel: PropTypes.string.isRequired,
    contentRange: PropTypes.string.isRequired,
    description: PropTypes.string,
    id: PropTypes.number.isRequired,
    imageUrl: PropTypes.string,
    priceLabel: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired
  })),
  labels: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired
};

PublicPartnerSkyworkEvents.defaultProps = {items: []};

export const PublicPartnerSkyworkSubscriptions = ({active, expiry, labels, onOpenOptions, showOptions}) => (
  <section className="replayer-club-page__journey replayer-club-page__subscriptions" aria-label={labels.title}>
    <SectionHeading eyebrow={labels.eyebrow} number={labels.number} title={labels.title} titleAccent={labels.titleAccent}/>
    <div className="replayer-club-page__subscription-panel">
      <div>
        <p>{labels.intro}</p>
        <strong>{active ? labels.active : labels.empty}</strong>
        {active && expiry && <small>{labels.activeUntil}</small>}
      </div>
      {showOptions && (
        <button type="button" className="replayer-club-page__journey-primary" onClick={onOpenOptions}>
          {labels.options}
        </button>
      )}
    </div>
  </section>
);

PublicPartnerSkyworkSubscriptions.propTypes = {
  active: PropTypes.bool.isRequired,
  expiry: PropTypes.string,
  labels: PropTypes.object.isRequired,
  onOpenOptions: PropTypes.func.isRequired,
  showOptions: PropTypes.bool.isRequired
};

PublicPartnerSkyworkSubscriptions.defaultProps = {expiry: null};

export const PublicPartnerSkyworkShop = ({labels, onOpen}) => (
  <section className="replayer-club-page__journey replayer-club-page__shop" aria-label={labels.title}>
    <SectionHeading eyebrow={labels.eyebrow} number={labels.number} title={labels.title} titleAccent={labels.titleAccent}/>
    <div className="replayer-club-page__shop-panel">
      <p>{labels.intro}</p>
      <button type="button" className="replayer-club-page__journey-primary" onClick={onOpen}>{labels.open}</button>
    </div>
  </section>
);

PublicPartnerSkyworkShop.propTypes = {
  labels: PropTypes.object.isRequired,
  onOpen: PropTypes.func.isRequired
};
